"use client";

import { animate } from "framer-motion";
import { createElement, useEffect, useRef, useState, type CSSProperties, type ElementType, type ReactNode } from "react";

/**
 * Trading Competition motion kit.
 *
 * Entrances are CSS keyframes keyed off a `data-reveal` scope: a block renders static (as QA-ed
 * against Figma), turns `armed` on mount (its `.tc-rv*` pieces hide) and `live` once it scrolls
 * into view (they play, each with its own `--d` delay). Without JS, or with reduced motion, the
 * scope never arms, so nothing hides and nothing moves.
 *
 * The keyframes animate the individual `translate` / `scale` / `filter` / `clip-path`
 * properties, never `transform`, so they compose with the Tailwind transform classes many of
 * these elements carry (centring, etc.) instead of overwriting them. Scopes must not nest.
 */
export type RevealState = "idle" | "armed" | "live";

export function useReveal<T extends HTMLElement = HTMLDivElement>({ threshold = 0.2, now = false } = {}) {
  const ref = useRef<T>(null);
  const [state, setState] = useState<RevealState>("idle");

  useEffect(() => {
    const el = ref.current;
    if (!el || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    setState("armed");
    if (now) {
      // two frames: let the armed (hidden) state paint before the keyframes start
      const id = requestAnimationFrame(() => requestAnimationFrame(() => setState("live")));
      return () => cancelAnimationFrame(id);
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setState("live");
        io.disconnect();
      },
      { threshold, rootMargin: "0px 0px -8% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold, now]);

  return [ref, state] as const;
}

/** A reveal scope as an element. */
export function Reveal({
  as = "div",
  threshold,
  now,
  className,
  style,
  children,
  id,
}: {
  as?: ElementType;
  threshold?: number;
  now?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
  id?: string;
}) {
  const [ref, state] = useReveal<HTMLElement>({ threshold, now });
  return createElement(as, { ref, id, className, style, "data-reveal": state }, children);
}

/** `style` for a reveal piece: start delay (s) and optional duration (s). */
export const at = (delay: number, duration?: number): CSSProperties =>
  ({ "--d": `${delay}s`, ...(duration ? { "--dur": `${duration}s` } : {}) }) as CSSProperties;

/**
 * Counts from 0 to the number inside `text` ("$200,000", "$3,000") once `live`, keeping its
 * prefix, suffix and thousands separators. Rests on `text` itself.
 */
export function useCountUp(text: string | undefined, live: boolean, { delay = 0, duration = 1.8 } = {}) {
  const [shown, setShown] = useState(text ?? "");
  useEffect(() => {
    setShown(text ?? "");
    if (!live || !text) return;
    // "$200,000" in English, "$200.000" in German and Portuguese: keep whichever separator the copy uses
    const m = text.match(/^(\D*)(\d{1,3}(?:([,.])\d{3})+|\d+)(\D*)$/);
    if (!m) return;
    const [, pre, num, sep, post] = m;
    const target = Number(sep ? num.split(sep).join("") : num);
    const fmt = (n: number) =>
      `${pre}${sep ? Math.round(n).toLocaleString("en-US").replace(/,/g, sep) : Math.round(n)}${post}`;
    setShown(fmt(0));
    const controls = animate(0, target, {
      delay,
      duration,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setShown(fmt(v)),
    });
    return () => controls.stop();
  }, [text, live, delay, duration]);
  return shown;
}

/**
 * Shows `shown` over an invisible copy of `text`, so a counting number never changes the width
 * (or the line box) of the line it sits in.
 */
export function StableText({ text, shown, align = "left" }: { text?: string; shown: string; align?: "left" | "right" }) {
  return (
    <span style={{ display: "inline-grid", fontFamily: "inherit" }}>
      <span aria-hidden="true" style={{ gridArea: "1 / 1", visibility: "hidden", fontFamily: "inherit" }}>
        {text}
      </span>
      <span style={{ gridArea: "1 / 1", textAlign: align, fontFamily: "inherit" }}>{shown}</span>
    </span>
  );
}

/** The keyframes and scope rules, rendered once by the page. */
export function MotionStyles() {
  return (
    <style jsx global>{`
      :root {
        --tc-ease: cubic-bezier(0.25, 0.1, 0.25, 1);
        --tc-expo: cubic-bezier(0.16, 1, 0.3, 1);
        --tc-draw: cubic-bezier(0.65, 0, 0.35, 1);
        --tc-spring: cubic-bezier(0.34, 1.56, 0.64, 1);
      }

      /* armed: everything that will enter starts hidden */
      :is([data-reveal="armed"] :is(.tc-rv-rise, .tc-rv-fade, .tc-rv-pop, .tc-rv-drop, .tc-rv-unveil), :is(.tc-rv-rise, .tc-rv-fade, .tc-rv-pop, .tc-rv-drop, .tc-rv-unveil)[data-reveal="armed"]) {
        opacity: 0;
      }
      :is([data-reveal="armed"] .tc-rv-paint, .tc-rv-paint[data-reveal="armed"]) {
        scale: 0 1;
      }
      /* (no clip-path while armed: Chrome's IntersectionObserver honours the target's own clip-path,
         so a fully clipped scope would never count as visible — opacity hides it instead) */
      :is([data-reveal="armed"] .tc-rv-draw, .tc-rv-draw[data-reveal="armed"]) {
        stroke-dashoffset: 1;
      }

      /* live: each piece plays on its own delay */
      :is([data-reveal="live"] .tc-rv-rise, .tc-rv-rise[data-reveal="live"]) {
        animation: tc-rise var(--dur, 0.9s) var(--tc-ease) var(--d, 0s) both;
      }
      :is([data-reveal="live"] .tc-rv-fade, .tc-rv-fade[data-reveal="live"]) {
        animation: tc-fade var(--dur, 1.2s) var(--tc-ease) var(--d, 0s) both;
      }
      :is([data-reveal="live"] .tc-rv-pop, .tc-rv-pop[data-reveal="live"]) {
        animation: tc-pop var(--dur, 0.7s) var(--tc-spring) var(--d, 0s) both;
      }
      :is([data-reveal="live"] .tc-rv-drop, .tc-rv-drop[data-reveal="live"]) {
        animation: tc-drop var(--dur, 0.5s) var(--tc-expo) var(--d, 0s) both;
      }
      :is([data-reveal="live"] .tc-rv-paint, .tc-rv-paint[data-reveal="live"]) {
        transform-origin: 0 50%;
        animation: tc-paint var(--dur, 0.75s) var(--tc-draw) var(--d, 0s) both;
      }
      :is([data-reveal="live"] .tc-rv-unveil, .tc-rv-unveil[data-reveal="live"]) {
        animation: tc-unveil var(--dur, 1.1s) var(--tc-expo) var(--d, 0s) both;
      }
      :is([data-reveal="live"] .tc-rv-zoom, .tc-rv-zoom[data-reveal="live"]) {
        animation: tc-zoom var(--dur, 1.6s) var(--tc-expo) var(--d, 0s) both;
      }
      :is([data-reveal="live"] .tc-rv-draw, .tc-rv-draw[data-reveal="live"]) {
        animation: tc-draw var(--dur, 0.55s) var(--tc-draw) var(--d, 0s) both;
      }

      /* leaderboard tease: the blurred board starts much blurrier and eases to the design's blur */
      :is([data-reveal="armed"] .tc-rv-unblur, .tc-rv-unblur[data-reveal="armed"]) {
        filter: blur(calc(var(--tc-blur) * 3.5));
        opacity: 0.35;
      }
      :is([data-reveal="live"] .tc-rv-unblur, .tc-rv-unblur[data-reveal="live"]) {
        animation: tc-unblur var(--dur, 1.8s) var(--tc-ease) var(--d, 0s) both;
      }
      @keyframes tc-unblur {
        from {
          filter: blur(calc(var(--tc-blur) * 3.5));
          opacity: 0.35;
        }
        to {
          filter: blur(var(--tc-blur));
          opacity: 1;
        }
      }

      /* "yet?": the selection's right bar becomes a blinking caret once it has landed */
      :is([data-reveal="live"] .tc-rv-drop.tc-caret, .tc-rv-drop.tc-caret[data-reveal="live"]) {
        animation:
          tc-drop var(--dur, 0.5s) var(--tc-expo) var(--d, 0s) both,
          tc-blink 1.1s steps(1, end) calc(var(--d, 0s) + 0.9s) infinite;
      }
      @keyframes tc-blink {
        0% {
          opacity: 1;
        }
        50% {
          opacity: 0;
        }
      }

      /* final CTA: the light shaft breathes, and "Get funded" catches a sheen now and then */
      .tc-beam {
        animation: tc-beam 5.5s ease-in-out infinite alternate;
      }
      @keyframes tc-beam {
        from {
          opacity: 0.35;
          scale: 0.94 1;
        }
        to {
          opacity: 1;
          scale: 1.04 1;
        }
      }
      .tc-sheen::after {
        content: "";
        position: absolute;
        top: -50%;
        bottom: -50%;
        left: 0;
        width: 35%;
        pointer-events: none;
        background: linear-gradient(90deg, transparent, rgba(11, 91, 69, 0.14), transparent);
        transform: skewX(-18deg);
        translate: -150% 0;
        animation: tc-sheen 6s var(--tc-ease) 2.5s infinite;
      }
      @keyframes tc-sheen {
        0% {
          translate: -150% 0;
        }
        22%,
        100% {
          translate: 420% 0;
        }
      }

      /* nav and top strip: a plain fade on load */
      .tc-intro-fade {
        animation: tc-fade 0.9s var(--tc-ease) both;
      }

      @keyframes tc-rise {
        from {
          opacity: 0;
          translate: 0 var(--rise, 2rem);
          filter: blur(var(--blur, 8px));
        }
        to {
          opacity: 1;
          translate: 0 0;
          filter: blur(0);
        }
      }
      @keyframes tc-fade {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
      @keyframes tc-pop {
        from {
          opacity: 0;
          scale: 0.6;
        }
        to {
          opacity: 1;
          scale: 1;
        }
      }
      @keyframes tc-drop {
        from {
          opacity: 0;
          translate: 0 0.75rem;
        }
        to {
          opacity: 1;
          translate: 0 0;
        }
      }
      @keyframes tc-paint {
        from {
          scale: 0 1;
        }
        to {
          scale: 1 1;
        }
      }
      @keyframes tc-unveil {
        from {
          opacity: 1;
          clip-path: inset(100% 0 0 0);
        }
        to {
          opacity: 1;
          clip-path: inset(0 0 0 0);
        }
      }
      @keyframes tc-zoom {
        from {
          scale: var(--zoom, 1.14);
        }
        to {
          scale: 1;
        }
      }
      @keyframes tc-draw {
        from {
          stroke-dashoffset: 1;
        }
        to {
          stroke-dashoffset: 0;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .tc-beam,
        .tc-sheen::after,
        .tc-intro-fade,
        [data-reveal],
        [data-reveal] * {
          animation: none !important;
        }
      }
    `}</style>
  );
}
